import type { ReactNode, ReactElement } from 'react';

// @mui
import { List, ListSubheader, Typography } from '@mui/material';
// hooks
import useResponsive from 'src/hooks/useResponsive';
// components
import { SkeletonConversationItem } from 'src/components/skeleton';
//
import ChatNavItem from './ChatNavItem';
import useChatClient from 'src/hooks/useChatClient';
import { useMemo } from 'react';
import _ from 'lodash';
import useAuth from 'src/hooks/useAuth';
import { useTranslation } from 'react-i18next';
import {
  getConversationType,
  isConversationVisibleInMainChat,
} from 'src/utils/conversations';

export interface ChatNavListProps {
  sx?: Record<string, any>;
  openNav?: boolean;
  onCloseNav?: (...args: any[]) => any;
  [key: string]: any;
}

// ----------------------------------------------------------------------

export default function ChatNavList({
  openNav,
  onCloseNav,
  sx,
  ...other
}: ChatNavListProps) {
  const { t } = useTranslation('translation');
  const isDesktop = useResponsive('up', 'md');

  const { user } = useAuth();
  const {
    conversations,
    activeConversation,
    pendingChat,
    chatLoading,
    setActiveConversationId,
    setPendingChat,
    setChatOpen,
    markRead,
  } = useChatClient();

  const [
    teamConversations,
    donationConversations,
    adminConversations,
    marketplaceConversations,
  ] = useMemo(() => {
    let teamConversations = [];
    let donationConversations = [];
    let adminConversations = [];
    let marketplaceConversations = [];

    for (const conversation of Object.values(conversations)) {
      // Buyer-side marketplace conversations live on the listing page and
      // the Marketplace > Messages inbox, not in the main chat.
      if (!isConversationVisibleInMainChat(conversation, user?.team_id)) {
        continue;
      }

      const type = getConversationType(conversation, user?.team_id);
      switch (type) {
        case 'TEAM':
          teamConversations.push(conversation);
          break;
        case 'ADMIN':
          adminConversations.push(conversation);
          break;
        case 'DONATION':
          donationConversations.push(conversation);
          break;
        case 'MARKETPLACE_LISTING':
          marketplaceConversations.push(conversation);
          break;
        default:
          // Handle unknown conversation types
          break;
      }
    }

    const lastActivity = (c) => c.lastMessage?.dateCreated ?? c.dateCreated;
    teamConversations = _.orderBy(teamConversations, lastActivity, 'desc');
    donationConversations = _.orderBy(
      donationConversations,
      lastActivity,
      'desc'
    );
    marketplaceConversations = _.orderBy(
      marketplaceConversations,
      lastActivity,
      'desc'
    );

    return [
      teamConversations,
      donationConversations,
      adminConversations,
      marketplaceConversations,
    ];
  }, [conversations, user?.team_id]);

  const renderConversation = (conversation) => {
    const conversationId = conversation.id;
    const activeId = activeConversation?.id;

    return (
      <ChatNavItem
        key={conversationId}
        openNav={openNav}
        conversation={conversation}
        isSelected={activeId === conversationId}
        onSelect={() => {
          if (!isDesktop) {
            onCloseNav();
          }
          setPendingChat(null);
          markRead(conversationId);
          setActiveConversationId(conversationId);
        }}
      />
    );
  };

  return (
    <List disablePadding sx={sx} {...other}>
      {chatLoading ? (
        Array.from({ length: 12 }).map((_, index) => (
          <SkeletonConversationItem key={index} />
        ))
      ) : (
        <>
          {user?.admin_chat_enabled && user?.is_admin ? (
            <>
              <ListSubheader>{t('chat.admin-chat')}</ListSubheader>
              {adminConversations.length > 0 ? (
                adminConversations.map(renderConversation)
              ) : (
                <ChatNavItem
                  key="pending-admin"
                  openNav={openNav}
                  conversation={{
                    id: 'pending-admin',
                    friendlyName: t('chat.admin-chat'),
                    lastMessage: null,
                    unreadCount: 0,
                    type: 'ADMIN',
                  }}
                  isSelected={pendingChat?.type === 'admin'}
                  onSelect={() => {
                    if (!isDesktop) onCloseNav();
                    setActiveConversationId(null);
                    setPendingChat({ type: 'admin' });
                    setChatOpen(true);
                  }}
                />
              )}
            </>
          ) : null}
          {user?.team_chat_enabled ? (
            <>
              <ListSubheader>{t('chat.team-chats')}</ListSubheader>
              {teamConversations.length > 0 ? (
                teamConversations.map(renderConversation)
              ) : (
                <ChatNavItem
                  key="pending-team"
                  openNav={openNav}
                  conversation={{
                    id: 'pending-team',
                    friendlyName: t('chat.team-chat'),
                    lastMessage: null,
                    unreadCount: 0,
                    type: 'TEAM',
                  }}
                  isSelected={pendingChat?.type === 'team'}
                  onSelect={() => {
                    if (!isDesktop) onCloseNav();
                    setActiveConversationId(null);
                    setPendingChat({ type: 'team' });
                    setChatOpen(true);
                  }}
                />
              )}
            </>
          ) : null}

          <ListSubheader>{t('chat.donation-chats')}</ListSubheader>
          {donationConversations.map(renderConversation)}
          {donationConversations.length === 0 && (
            <Typography
              color="text.secondary"
              variant="body2"
              sx={{ w: '100%', textAlign: 'center' }}
            >
              {t('chat.no-donation-chats-found')}
            </Typography>
          )}

          {marketplaceConversations.length > 0 && (
            <>
              <ListSubheader>
                {t('chat.marketplace-chats', {
                  defaultValue: 'Marketplace Chats',
                })}
              </ListSubheader>
              {marketplaceConversations.map(renderConversation)}
            </>
          )}
        </>
      )}
    </List>
  );
}
